import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { notify } from "@/lib/notify";

import LeftPanel from "./LeftPanel";
import RightPanel from "./RightPanel";
import Canvas from "./Canvas";
import FloatingToolbar from "./Toolbar";
import EditorHeader from "./EditorHeader";
import ShareDialog from "./ShareDialog";
import StatusBar from "./StatusBar";
import CanvasSettings from "./CanvasSettings";
import MultiSelectBar from "./MultiSelectBar";
import ResizeHandle from "./ResizeHandle";

import { useMediaQuery } from "./hooks/useMediaQuery";
import { useHistory } from "./hooks/useHistory";
import { useElementActions, readFileAsDataURL, trimTransparentPadding } from "./hooks/useElementActions";
import { unionBbox, bboxOf } from "./canvasUtils";
import { nanoIdLike } from "./utils";
import { generationDuration } from "./animation";
import { DEFAULT_ELEMENT_COLOR } from "./drawing";
import { exportProjectZip, exportSvgOnly, buildShareLink, parseShareLink } from "./exportUtils";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || "http://localhost:5000";

const DEFAULT_META = { width: 800, height: 560, background: "transparent" };

export default function Editor() {
  const [elements, setElements] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [tool, setTool] = useState("select");
  const [playing, setPlaying] = useState(true);
  const [toolbarCollapsed, setToolbarCollapsed] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [sourceOpen, setSourceOpen] = useState(false);
  const [shareLink, setShareLink] = useState("");
  const [meta, setMeta] = useState(DEFAULT_META);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [replayNonce, setReplayNonce] = useState({});
  // Ids currently playing the one-shot generation effect (typewriter / draw /
  // scale). Cleared per-id once the effect finishes so the element reverts to
  // its normal render (and any configured looping animation resumes).
  const [genIds, setGenIds] = useState(() => new Set());
  const genTimers = useRef({});

  // In-canvas text editing: `draftText` holds a freshly inserted text element
  // (not yet committed to history); `editingTextId` is the id of an existing
  // text element being re-edited. The element shown in the editor overlay is
  // whichever is active.
  const [draftText, setDraftText] = useState(null);
  const [editingTextId, setEditingTextId] = useState(null);

  // Image crop mode: when set, this image id is being cropped interactively.
  // Alt/Opt + double-click a selected image enters crop mode; double-clicking
  // empty canvas exits. The crop overlay lives in Canvas; the state is held
  // here so selection / commit logic stays centralized.
  const [cropId, setCropId] = useState(null);

  // Imperative handle exposed by Canvas (e.g. current viewport center in world coords).
  const canvasApiRef = useRef(null);

  // ===== Responsive layout state =====
  const isCompact = useMediaQuery("(max-width: 1023px)");
  const [leftOpen, setLeftOpen] = useState(true);
  const [rightOpen, setRightOpen] = useState(true);
  const [leftWidth, setLeftWidth] = useState(() => Number(localStorage.getItem("svg-studio-left-w")) || 256);
  const [rightWidth, setRightWidth] = useState(() => Number(localStorage.getItem("svg-studio-right-w")) || 288);
  const [resizing, setResizing] = useState(false);
  const PANEL_EASE = "width 0.38s cubic-bezier(0.22,1,0.36,1), margin 0.38s cubic-bezier(0.22,1,0.36,1)";

  useEffect(() => { localStorage.setItem("svg-studio-left-w", String(leftWidth)); }, [leftWidth]);
  useEffect(() => { localStorage.setItem("svg-studio-right-w", String(rightWidth)); }, [rightWidth]);
  useEffect(() => {
    if (isCompact) { setLeftOpen(false); setRightOpen(false); }
    else { setLeftOpen(true); setRightOpen(true); }
  }, [isCompact]);

  // On small screens the panels are full-height overlays that sit above the
  // canvas, so a library tile cannot be dropped onto it. Collapse the panels
  // the moment a drag starts to expose the canvas as a drop target.
  const handleTileDragStart = useCallback(() => {
    if (isCompact) { setLeftOpen(false); setRightOpen(false); }
  }, [isCompact]);

  const { commit, checkpoint, setLive, undo, redo, canUndo, canRedo } = useHistory(elements, setElements);
  const {
    updateElement,
    deleteElement,
    deleteElements,
    duplicateElement,
    duplicateElements,
    duplicateForDrag,
    alignElements,
    groupElements,
    ungroupElements,
    bringToFront,
    handleCanvasDrop,
    applyAnimation,
    addImageFromFile,
  } = useElementActions({ commit, selectedIds, setSelectedIds });

  const liveUpdate = useCallback(
    (id, attrs, type) =>
      setLive((prev) => prev.map((el) => (el.id === id ? { ...el, attrs, ...(type ? { type } : {}) } : el))),
    [setLive]
  );

  // Enter crop mode for an image (Alt/Opt + double-click). Only images support
  // cropping; other element types are ignored. If the image has no crop set
  // yet (orig box equals the display box), seed a centered 80% crop rect so
  // the user has room to move and resize it immediately — a full-size rect
  // can't be moved (it already fills the image bounds). The crop fractions are
  // relative to the orig box, and the display box (x/y/width/height) is
  // updated to match so the selection bbox reflects the crop.
  const enterCropMode = useCallback((id) => {
    const el = elements.find((e) => e.id === id);
    if (!el || el.type !== "image") return;
    const ox = el.attrs.origX ?? el.attrs.x ?? 0;
    const oy = el.attrs.origY ?? el.attrs.y ?? 0;
    const ow = el.attrs.origW ?? el.attrs.width ?? 0;
    const oh = el.attrs.origH ?? el.attrs.height ?? 0;
    const hasCrop = ox !== (el.attrs.x ?? 0) || oy !== (el.attrs.y ?? 0) ||
      ow !== (el.attrs.width ?? 0) || oh !== (el.attrs.height ?? 0);
    if (!hasCrop) {
      const cx = 0.1, cy = 0.1, cw = 0.8, ch = 0.8;
      commit((prev) => prev.map((e) => e.id === id ? {
        ...e,
        attrs: {
          ...e.attrs,
          origX: ox, origY: oy, origW: ow, origH: oh,
          cropX: cx, cropY: cy, cropW: cw, cropH: ch,
          x: ox + cx * ow,
          y: oy + cy * oh,
          width: cw * ow,
          height: ch * oh,
        },
      } : e));
    }
    setCropId(id);
  }, [elements, commit]);

  // Exit crop mode. Checkpoint so the crop result is committed to history
  // (supports undo); the live crop edits were applied via setLive and need a
  // history entry to become undoable. Then clear the crop flag.
  const exitCropMode = useCallback(() => {
    checkpoint();
    setCropId(null);
  }, [checkpoint]);

  // Live update for line-family editing: persists the `lineEdit` flag so a line
  // that becomes a <path> (when arced/folded) stays editable by the overlay.
  const liveUpdateLine = useCallback(
    (id, attrs, type) =>
      setLive((prev) =>
        prev.map((el) =>
          el.id === id ? { ...el, attrs, ...(type ? { type } : {}), lineEdit: type === "path" } : el
        )
      ),
    [setLive]
  );

  const liveUpdateMany = useCallback(
    (updates) =>
      setLive((prev) => {
        const map = Object.create(null);
        updates.forEach((u) => {
          map[u.id] = u;
        });
        return prev.map((el) =>
          map[el.id]
            ? { ...el, attrs: map[el.id].attrs, ...(map[el.id].type ? { type: map[el.id].type } : {}) }
            : el
        );
      }),
    [setLive]
  );

  const primaryId = selectedIds.length === 1 ? selectedIds[0] : null;
  const selected = useMemo(() => elements.find((e) => e.id === primaryId) || null, [elements, primaryId]);
  const selectedEls = useMemo(() => {
    const set = new Set(selectedIds);
    return elements.filter((e) => set.has(e.id));
  }, [elements, selectedIds]);

  // Bounding box of all content; the canvas is an infinite plane, so this is
  // what defines the meaningful "size" of a project (used by the status bar).
  const contentBox = useMemo(() => (elements.length ? unionBbox(elements) : null), [elements]);

  // Expand a set of ids so whole groups are selected together.
  const expandGroup = useCallback(
    (ids) => {
      const out = new Set();
      ids.forEach((id) => {
        const el = elements.find((e) => e.id === id);
        if (el?.groupId) elements.forEach((e) => e.groupId === el.groupId && out.add(e.id));
        else out.add(id);
      });
      return [...out];
    },
    [elements]
  );

  const computeNext = useCallback(
    (prev, id, additive) => {
      const grp = expandGroup([id]);
      if (additive) {
        const allIn = grp.every((g) => prev.includes(g));
        return allIn ? prev.filter((p) => !grp.includes(p)) : [...new Set([...prev, ...grp])];
      }
      if (prev.includes(id)) return prev; // keep selection so a drag moves the group
      return grp;
    },
    [expandGroup]
  );

  const handleSelect = useCallback(
    (id, additive = false) => {
      if (id == null) {
        setSelectedIds([]);
        return;
      }
      setSelectedIds((prev) => computeNext(prev, id, additive));
    },
    [computeNext]
  );

  // Synchronously update selection AND return the elements that should be dragged.
  const resolveDragTargets = useCallback(
    (id, additive) => {
      let next = [];
      setSelectedIds((prev) => {
        next = computeNext(prev, id, additive);
        return next;
      });
      const set = new Set(next);
      return elements.filter((e) => set.has(e.id));
    },
    [computeNext, elements]
  );

  const handleMarquee = useCallback(
    (ids, additive = false) => {
      const grp = expandGroup(ids);
      if (additive) setSelectedIds((prev) => [...new Set([...prev, ...grp])]);
      else setSelectedIds(grp);
    },
    [expandGroup]
  );

  const replay = useCallback((ids) => {
    if (!ids || !ids.length) return;
    setPlaying(true);
    setReplayNonce((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        next[id] = (next[id] || 0) + 1;
      });
      return next;
    });
    // Mark these ids as generating, and schedule each to clear after its own
    // effect duration (typewriter length scales with text; draw/scale with size).
    setGenIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
    setElements((curr) => {
      ids.forEach((id) => {
        const el = curr.find((e) => e.id === id);
        if (!el) return;
        const ms = (generationDuration(el, bboxOf(el)) + 0.08) * 1000;
        if (genTimers.current[id]) clearTimeout(genTimers.current[id]);
        genTimers.current[id] = setTimeout(() => {
          delete genTimers.current[id];
          setGenIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        }, ms);
      });
      return curr;
    });
  }, [setElements]);

  useEffect(() => () => { Object.values(genTimers.current).forEach(clearTimeout); }, []);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const canUngroup = useMemo(
    () => elements.some((e) => selectedSet.has(e.id) && e.groupId),
    [elements, selectedSet]
  );

  useEffect(() => {
    const data = parseShareLink();
    if (data?.elements) {
      setElements(data.elements);
      if (data.meta) setMeta((m) => ({ ...m, ...data.meta }));
      notify.success("Loaded shared project");
    }
  }, []);

  // Selecting a shape tool now only records the shape type to draw; the actual
  // shape is created by dragging a live preview on the canvas (see Canvas +
  // useShapeDraw). All other tools (select/pen/pencil) keep their behaviour.
  const onToolClick = useCallback((id) => {
    setTool(id);
  }, []);

  const addDrawnElement = useCallback(
    (el) => {
      commit((prev) => [...prev, el]);
      setSelectedIds([el.id]);
    },
    [commit]
  );

  // Commit a shape drawn by dragging: add it, select it, then return to the
  // select tool so it can be manipulated immediately (matches prior UX).
  const addDrawnShape = useCallback(
    (el) => {
      commit((prev) => [...prev, el]);
      setSelectedIds([el.id]);
      setTool("select");
    },
    [commit]
  );

  // ===== In-canvas text tool =====
  const editingTextEl = useMemo(
    () => draftText || elements.find((e) => e.id === editingTextId) || null,
    [draftText, editingTextId, elements]
  );

  // Text tool click on empty surface: insert a blinking caret at that point.
  const handleTextCreate = useCallback((pt) => {
    const fontSize = 32;
    const el = {
      id: nanoIdLike(),
      type: "text",
      attrs: {
        x: pt.x,
        y: pt.y + fontSize * 0.85,
        fill: DEFAULT_ELEMENT_COLOR,
        fontSize,
        fontFamily: "IBM Plex Sans",
        textAnchor: "start",
        text: "",
      },
      animation: null,
    };
    setEditingTextId(null);
    setDraftText(el);
    setSelectedIds([]);
    setTool("select");
  }, []);

  // Enter edit mode for an existing text element (single-click w/ text tool or
  // double-click w/ select tool).
  const handleStartTextEdit = useCallback((el) => {
    setDraftText(null);
    setSelectedIds([el.id]);
    setEditingTextId(el.id);
  }, []);

  // Finish editing (blur / Enter / Escape): commit non-empty text, otherwise
  // discard a draft or delete an emptied existing element.
  const handleTextCommit = useCallback(
    (rawText) => {
      let text = (rawText || "").replace(/\r/g, "").replace(/\n+$/, "");
      const isEmpty = text.trim() === "";
      const draft = draftText;
      const editId = editingTextId;
      setDraftText(null);
      setEditingTextId(null);
      if (draft) {
        if (!isEmpty) {
          const el = { ...draft, attrs: { ...draft.attrs, text } };
          commit((prev) => [...prev, el]);
          setSelectedIds([el.id]);
        }
        return;
      }
      if (editId) {
        if (isEmpty) {
          deleteElement(editId);
        } else {
          updateElement(editId, { attrs: { ...(elements.find((e) => e.id === editId)?.attrs || {}), text } });
        }
      }
    },
    [draftText, editingTextId, commit, updateElement, deleteElement, elements]
  );

  const handleExportZip = useCallback(async () => {
    if (elements.length === 0) return notify.error("Canvas is empty");
    await exportProjectZip(elements, { ...meta, name: "svg-studio-export" });
    notify.success("Exported project zip");
  }, [elements, meta]);

  const handleExportSvg = useCallback(() => {
    if (elements.length === 0) return notify.error("Canvas is empty");
    exportSvgOnly(elements, { ...meta, name: "svg-studio" });
    notify.success("Exported .svg");
  }, [elements, meta]);

  const handleShare = useCallback(() => {
    if (elements.length === 0) return notify.error("Canvas is empty");
    setShareLink(buildShareLink(elements, { w: meta.width, h: meta.height, background: meta.background }));
    setShareOpen(true);
  }, [elements, meta]);

  const copyShareLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      notify.success("Link copied to clipboard");
    } catch {
      notify.error("Copy failed — select & copy manually");
    }
  }, [shareLink]);

  const handleDownloadSource = useCallback(() => {
    setSourceOpen(true);
  }, []);

  const togglePlay = useCallback(() => {
    // Pause when playing; when starting playback, also fire the generation
    // effect (typewriter for text, draw/scale for shapes) for the selection
    // (or everything when nothing is selected).
    if (playing) {
      setPlaying(false);
      return;
    }
    const ids = selectedIds.length ? selectedIds : elements.map((e) => e.id);
    replay(ids);
  }, [playing, selectedIds, elements, replay]);
  const recenterView = useCallback(() => {
    canvasApiRef.current?.recenter?.();
  }, []);

  // Global paste handler: insert pasted images (from clipboard) onto the canvas
  // at the viewport center. Ignored while editing text in-canvas or when the
  // user is typing in an input/textarea/contentEditable.
  useEffect(() => {
    const onPaste = (e) => {
      const active = document.activeElement;
      const tag = active?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || active?.isContentEditable) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      let imageItem = null;
      for (const it of items) {
        if (it.type && it.type.startsWith("image/")) {
          imageItem = it;
          break;
        }
      }
      if (!imageItem) return;
      e.preventDefault();
      const file = imageItem.getAsFile();
      if (!file) return;
      const point = canvasApiRef.current?.getCenter?.() || { x: 400, y: 280 };
      addImageFromFile(file, point);
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [addImageFromFile]);

  // Replace an existing image element's source with a newly picked file,
  // preserving its position / size / styling. Transparent padding is trimmed
  // so the new content fills the element box consistently.
  const replaceImage = useCallback(
    async (id, file) => {
      try {
        const rawDataUrl = await readFileAsDataURL(file);
        const { dataUrl } = await trimTransparentPadding(rawDataUrl);
        updateElement(id, { attrs: { ...((elements.find((e) => e.id === id)?.attrs) || {}), href: dataUrl } });
      } catch (err) {
        toast.error(err.message || "无法替换图片");
      }
    },
    [elements, updateElement]
  );
  const toggleCanvasExpand = useCallback(() => {
    if (leftOpen || rightOpen) {
      setLeftOpen(false);
      setRightOpen(false);
    } else if (!isCompact) {
      setLeftOpen(true);
      setRightOpen(true);
    }
  }, [leftOpen, rightOpen, isCompact]);
  const handleClear = useCallback(() => {
    if (elements.length) commit([]);
    setSelectedIds([]);
  }, [commit, elements.length]);

  const replaySelected = useCallback(() => {
    const ids = selectedIds.length ? selectedIds : elements.map((e) => e.id);
    replay(ids);
  }, [selectedIds, elements, replay]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.isContentEditable) return;
      if ((e.metaKey || e.ctrlKey) && (e.key === "g" || e.key === "G")) {
        e.preventDefault();
        if (e.shiftKey) ungroupElements(selectedIds);
        else if (selectedIds.length > 1) groupElements(selectedIds);
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "z") {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      } else if ((e.metaKey || e.ctrlKey) && (e.key === "d" || e.key === "D")) {
        if (selectedIds.length) {
          e.preventDefault();
          duplicateElements(selectedIds);
        }
      } else if (e.key === "Delete" || e.key === "Backspace") {
        if (selectedIds.length) {
          e.preventDefault();
          deleteElements(selectedIds);
        }
      } else if (e.key === "Escape") {
        setSelectedIds([]);
      } else if (e.key === "Home" || (e.shiftKey && e.key === "1")) {
        e.preventDefault();
        canvasApiRef.current?.recenter?.();
      } else if (e.key === "v" || e.key === "V") {
        setTool("select");
      } else if (e.key === "p" || e.key === "P") {
        setTool("pen");
      } else if (e.key === "b" || e.key === "B") {
        setTool("pencil");
      } else if (e.key === "r" || e.key === "R") {
        onToolClick("rect");
      } else if (e.key === "c" || e.key === "C") {
        onToolClick("circle");
      } else if (e.key === "l" || e.key === "L") {
        onToolClick("line");
      } else if (e.key === "t" || e.key === "T") {
        onToolClick("text");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo, deleteElements, duplicateElements, groupElements, ungroupElements, selectedIds, onToolClick]);

  return (
    <div className="editor-shell flex flex-col h-[100dvh] overflow-hidden text-ink p-3 gap-3">
      <EditorHeader
        objectCount={elements.length}
        onDownloadSource={handleDownloadSource}
        onShare={handleShare}
        onExportSvg={handleExportSvg}
        onExportZip={handleExportZip}
        leftOpen={leftOpen}
        rightOpen={rightOpen}
        toggleLeft={() => setLeftOpen((o) => !o)}
        toggleRight={() => setRightOpen((o) => !o)}
        isCompact={isCompact}
      />

      <main className="flex flex-1 overflow-hidden relative min-h-0">
        {!isCompact && (
          <div
            className="relative shrink-0 h-full overflow-hidden"
            style={{
              width: leftOpen ? leftWidth : 0,
              marginRight: leftOpen ? 12 : 0,
              transition: resizing ? "none" : PANEL_EASE,
            }}
          >
            <div className="absolute top-0 left-0 h-full" style={{ width: leftWidth }}>
              <LeftPanel onApplyAnimation={applyAnimation} onAddImage={addImageFromFile} onTileDragStart={handleTileDragStart} />
            </div>
            {leftOpen && (
              <ResizeHandle
                side="left"
                width={leftWidth}
                setWidth={setLeftWidth}
                min={200}
                max={420}
                onResizeStart={() => setResizing(true)}
                onResizeEnd={() => setResizing(false)}
              />
            )}
          </div>
        )}

        <section className="floating-canvas flex-1 relative overflow-hidden min-w-0 rounded-2xl">
          <Canvas
            elements={elements}
            selectedIds={selectedIds}
            onSelect={handleSelect}
            onMarquee={handleMarquee}
            resolveDragTargets={resolveDragTargets}
            onLiveUpdate={liveUpdate}
            onLiveUpdateLine={liveUpdateLine}
            onLiveUpdateMany={liveUpdateMany}
            onDragStart={checkpoint}
            onCanvasDrop={handleCanvasDrop}
            playing={playing}
            width={meta.width}
            height={meta.height}
            background={meta.background}
            tool={tool}
            onCommitDrawn={addDrawnElement}
            onCommitShape={addDrawnShape}
            onEditCommit={updateElement}
            onResizeStart={checkpoint}
            replayNonce={replayNonce}
            generatingIds={genIds}
            apiRef={canvasApiRef}
            canvasExpanded={!leftOpen && !rightOpen}
            onToggleExpand={toggleCanvasExpand}
            editingTextEl={editingTextEl}
            onTextCreate={handleTextCreate}
            onStartTextEdit={handleStartTextEdit}
            onTextCommit={handleTextCommit}
            cropId={cropId}
            onEnterCrop={enterCropMode}
            onExitCrop={exitCropMode}
            onDuplicateForDrag={duplicateForDrag}
            onBringToFront={bringToFront}
          />
          <FloatingToolbar
            tool={tool}
            setTool={onToolClick}
            playing={playing}
            togglePlay={togglePlay}
            onReplay={replaySelected}
            onUndo={undo}
            onRedo={redo}
            canUndo={canUndo}
            canRedo={canRedo}
            onClear={handleClear}
            collapsed={toolbarCollapsed}
            toggleCollapsed={() => setToolbarCollapsed((c) => !c)}
            onOpenSettings={() => setSettingsOpen((s) => !s)}
            onRecenter={recenterView}
          />
          {selectedIds.length > 1 && (
            <MultiSelectBar
              count={selectedIds.length}
              canUngroup={canUngroup}
              onAlign={(mode) => alignElements(selectedIds, mode)}
              onGroup={() => groupElements(selectedIds)}
              onUngroup={() => ungroupElements(selectedIds)}
              onDuplicate={() => duplicateElements(selectedIds)}
              onDelete={() => deleteElements(selectedIds)}
            />
          )}
          <CanvasSettings open={settingsOpen} onClose={() => setSettingsOpen(false)} meta={meta} setMeta={setMeta} />
          <StatusBar tool={tool} contentBox={contentBox} playing={playing} selectedCount={selectedIds.length} />
        </section>

        {!isCompact && (
          <div
            className="relative shrink-0 h-full overflow-hidden"
            style={{
              width: rightOpen ? rightWidth : 0,
              marginLeft: rightOpen ? 12 : 0,
              transition: resizing ? "none" : PANEL_EASE,
            }}
          >
            <div className="absolute top-0 right-0 h-full" style={{ width: rightWidth }}>
              <RightPanel
                element={selected}
                selectedElements={selectedEls}
                allElements={elements}
                background={meta.background}
                onUpdate={updateElement}
                onDelete={deleteElement}
                onDuplicate={duplicateElement}
                onReplay={() => replay([primaryId])}
                onReplaceImage={replaceImage}
              />
            </div>
            {rightOpen && (
              <ResizeHandle
                side="right"
                width={rightWidth}
                setWidth={setRightWidth}
                min={240}
                max={460}
                onResizeStart={() => setResizing(true)}
                onResizeEnd={() => setResizing(false)}
              />
            )}
          </div>
        )}

        {isCompact && (
          <>
            {(leftOpen || rightOpen) && (
              <div
                data-testid="panel-backdrop"
                className="absolute inset-0 bg-black/50 z-40"
                onClick={() => { setLeftOpen(false); setRightOpen(false); }}
              />
            )}
            <div
              className={`absolute top-0 bottom-0 left-0 z-50 shadow-2xl transition-transform duration-300 ${leftOpen ? "translate-x-0" : "-translate-x-full"}`}
              style={{ width: "min(86vw, 320px)" }}
            >
              <LeftPanel onApplyAnimation={applyAnimation} onAddImage={addImageFromFile} onTileDragStart={handleTileDragStart} isCompact onClose={() => setLeftOpen(false)} />
            </div>
            <div
              className={`absolute top-0 bottom-0 right-0 z-50 shadow-2xl transition-transform duration-300 ${rightOpen ? "translate-x-0" : "translate-x-full"}`}
              style={{ width: "min(86vw, 320px)" }}
            >
              <RightPanel
                element={selected}
                selectedElements={selectedEls}
                allElements={elements}
                background={meta.background}
                onUpdate={updateElement}
                onDelete={deleteElement}
                onDuplicate={duplicateElement}
                onReplay={() => replay([primaryId])}
                onReplaceImage={replaceImage}
                isCompact
                onClose={() => setRightOpen(false)}
              />
            </div>
          </>
        )}
      </main>

      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} shareLink={shareLink} onCopy={copyShareLink} />
    </div>
  );
}
